import axios from 'axios';
import { Platform } from 'react-native';
const Config = require('~config/default.env').default;
import AsyncStorage from '~global/storage';
import Toast from 'react-native-toast-message';
import * as Linking from 'expo-linking';
import DeviceInfo, { getVersion } from 'react-native-device-info';
import NetInfo from '@react-native-community/netinfo';
import { fetchSession } from '~global/sociolla.dm.lib/sociolla-analytics';
import insiderTracking from '~global/sociolla.dm.lib/platform/insider-tracking';
import { dropCredentialsOnCentralLoginLegalPage } from '~global/utils/central-auth';
import analyticsHitApi from '~global/utils/analytics-hit-api';
import { SOC } from '~global/constants';
import { storeAsyncData } from '~global/helpers';
import { isWebPlatform, isNative } from '~global/screens';
import { screenHeight, screenWidth } from '~global/screens';

let Adjust;
let UserAgent;

if (!isNative) {
  Adjust = require('@adjustcom/adjust-web-sdk').default;
  UserAgent = {};
} else {
  Adjust = require('react-native-adjust').Adjust;
  UserAgent = require('react-native-user-agent');
}

let lastUsedDomain = 0;
let lastUsedDomainCart = 0;
let interceptorsRegistered = false;

const requestCache = new Map();
const THROTTLE_THRESHOLD_MS = 2000; // 2 seconds

const THROTTLED_ENDPOINTS = [
  '/user/me/carts',
  '/users/me/wishlists',
  '/search',
  '/baby-registry',
  '/v3/product-views',
];

const shouldThrottleEndpoint = (url, method) => {
  return THROTTLED_ENDPOINTS.some((endpoint) => {
    if (endpoint === '/user/me/carts') {
      return url === endpoint && method === 'get';
    }
    return url.includes(endpoint);
  });
};

const generateCacheKey = (config) => {
  const { url, method, params, data } = config;

  const paramsKey = params ? JSON.stringify(sortObjectKeys(params)) : '';
  const dataKey = data ? JSON.stringify(sortObjectKeys(data)) : '';

  return `${method || 'GET'}-${url}-${paramsKey}-${dataKey}`;
};

const sortObjectKeys = (obj) => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return obj;
  }

  const sortedObj = {};
  Object.keys(obj)
    .sort()
    .forEach((key) => {
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        sortedObj[key] = sortObjectKeys(obj[key]);
      } else {
        sortedObj[key] = obj[key];
      }
    });

  return sortedObj;
};

const deviceDataCache = async () => {
  const state = await NetInfo.fetch();
  const platform = SOC[Platform.OS];
  const connectionType = state.type;

  const deviceData = {
    os: DeviceInfo.getSystemName(),
    os_version: DeviceInfo.getSystemVersion(),
    environment: Config.ADJUST_ENV ? 'UAT_LILLA_APP' : 'PROD_LILLA_APP',
    platform: 'lulla-' + platform,
    user_agent: platform,
    language: 'id',
    app_version: DeviceInfo.getVersion(),
    brand: DeviceInfo.getBrand(),
    model: DeviceInfo.getModel(),
    screen_width: screenWidth,
    screen_height: screenHeight,
    internet_connection_type: connectionType,
    country: 'Indonesia',
    build: DeviceInfo.getBuildNumber(),
    disable_ad_network_callouts: true,
  };

  if (!isNative) {
    deviceData.os = Platform.OS;
    deviceData.os_version = navigator?.userAgent || '';
    deviceData.brand = navigator?.vendor || '';
    deviceData.model = navigator?.platform || '';
    deviceData.platform = 'lilla';
    deviceData.environment = Config.ADJUST_ENV ? 'UAT_LILLA' : 'PROD_LILLA';
  }

  if (isNative) {
    deviceData.user_agent = await UserAgent.getUserAgent();
    if (Platform.OS === 'android') {
      deviceData.sdk_version = Platform.constants.Version;
    }
  }

  return deviceData;
};

export default function setup() {
  if (interceptorsRegistered) {
    return;
  }
  interceptorsRegistered = true;

  axios.interceptors.request.use(
    async (config) => {
      let shouldSetAdjustId = false;

      const platform = SOC[Platform.OS];
      const deviceData = await deviceDataCache();

      if (deviceData && /auth[/]login/.test(config.url)) {
        config.headers['device_data'] = JSON.stringify(deviceData);
      }

      config.withCredentials = true;
      dropCredentialsOnCentralLoginLegalPage(config);
      if (config.url.includes(Config.API_GOOGLE)) {
        return config;
      }

      if (/android|ios/.test(platform)) {
        config.headers['User-Agent'] = platform;
        config.headers['mobapp-version'] = getVersion();
      }

      config.headers['SOC-PLATFORM'] = platform;

      if (isWebPlatform) {
        config.headers['session_id'] = fetchSession();
      } else {
        const userData = JSON.parse(await AsyncStorage.getItem('userData'));
        if (
          userData &&
          userData.token &&
          !config.url.includes('amazonaws.com')
        ) {
          config.headers.Authorization = `Bearer ${userData.token}`;
        }
        try {
          let d = new Date().getTime();
          let newGuid = '';
          const guid = await AsyncStorage.getItem('SOCIOLLA_GUID');
          if (!guid) {
            if (
              typeof performance !== 'undefined' &&
              // eslint-disable-next-line no-undef
              typeof performance.now === 'function'
            ) {
              // eslint-disable-next-line no-undef
              d += performance.now(); //use high-precision timer if available
            }
            newGuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
              /[xy]/g,
              function (c) {
                const r = (d + Math.random() * 16) % 16 | 0;
                d = Math.floor(d / 16);
                return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
              },
            );
            AsyncStorage.setItem('SOCIOLLA_GUID', newGuid);
            if (config.url) {
              insiderTracking.insiderLoginEvent(newGuid);
            }
          } else {
            newGuid = await AsyncStorage.getItem('SOCIOLLA_GUID');
          }

          config.headers['session_id'] = fetchSession();
        } catch (e) {
          console.error(e);
        }
      }
      // Request throttling logic
      if (shouldThrottleEndpoint(config.url, config.method)) {
        const cacheKey = generateCacheKey(config);
        const currentTime = Date.now();
        const cachedRequest = requestCache.get(cacheKey);

        if (
          cachedRequest &&
          currentTime - cachedRequest.timestamp < THROTTLE_THRESHOLD_MS
        ) {
          if (Config.ENABLE_NETWORK_LOGGER) {
            console.debug(
              `Request throttled: ${config.url}, returning cached response`,
            );
          }

          if (cachedRequest.response) {
            const clonedResponse = JSON.parse(
              JSON.stringify(cachedRequest.response),
            );

            clonedResponse._internal_warning =
              'Twice hit within throttle window';

            return Promise.reject({
              __cachedResponse: {
                ...clonedResponse,
                config,
                data: clonedResponse.data
                  ? {
                      ...clonedResponse.data,
                      _internal_warning: 'Twice hit within throttle window',
                    }
                  : { _internal_warning: 'Twice hit within throttle window' },
              },
            });
          }

          config._isDuplicateRequest = true;
        }

        requestCache.set(cacheKey, {
          timestamp: currentTime,
          config,
        });

        setTimeout(() => {
          requestCache.delete(cacheKey);
        }, THROTTLE_THRESHOLD_MS);
      }

      // Track API request
      if (Config.ENABLE_NETWORK_LOGGER) {
        analyticsHitApi.track(
          config.url,
          config.params,
          config.data,
          config.method,
        );
      }

      if (config.url.startsWith(Config.MS_CATALOG_API_URL)) {
        config.url =
          Config.MS_CATALOG_API_LIST[lastUsedDomain] +
          config.url.substr(Config.MS_CATALOG_API_URL.length);
        lastUsedDomain =
          lastUsedDomain + 1 >= Config.MS_CATALOG_API_LIST.length
            ? 0
            : lastUsedDomain + 1;
      }

      if (config.url.startsWith(Config.MS_CART_API_URL)) {
        config.url =
          Config.MS_CART_API_LIST[lastUsedDomainCart] +
          config.url.substr(Config.MS_CART_API_URL.length);
        lastUsedDomainCart =
          lastUsedDomainCart + 1 >= Config.MS_CART_API_LIST.length
            ? 0
            : lastUsedDomainCart + 1;
      }

      if (isNative) {
        shouldSetAdjustId =
          /[/]sessions[/]me/.test(config.url) ||
          /[/]users[/]me[/]orders/.test(config.url);
      } else {
        shouldSetAdjustId = true;
      }

      if (shouldSetAdjustId) {
        let adjustDeviceId;
        let adjustAdvertisingId;

        const withAdjustTimeout = (promise, ms = 3000) =>
          Promise.race([
            promise,
            new Promise((resolve) => setTimeout(() => resolve(null), ms)),
          ]);

        if (isNative) {
          adjustDeviceId = await withAdjustTimeout(
            new Promise((resolve) => {
              Adjust.getAdid((adjustId) => resolve(adjustId));
            }),
          );

          if (Platform.OS === 'ios') {
            adjustAdvertisingId = await withAdjustTimeout(
              new Promise((resolve) => {
                Adjust.getIdfa((idfa) => resolve(idfa));
              }),
            );
          }

          if (Platform.OS === 'android') {
            adjustAdvertisingId = await withAdjustTimeout(
              new Promise((resolve) => {
                Adjust.getGoogleAdId((adId) => resolve(adId));
              }),
            );
          }
        } else {
          adjustAdvertisingId = await AsyncStorage.getItem('adjust_web_uuid');
          adjustDeviceId = await AsyncStorage.getItem('adjust_device_id');
        }

        config.headers['adjust_device_id'] = adjustDeviceId;
        config.headers['adjust_advertising_id'] = adjustAdvertisingId;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  // Add response interceptors
  axios.interceptors.response.use(
    (response) => {
      // Store the response in the cache for throttled endpoints
      if (shouldThrottleEndpoint(response.config.url)) {
        const cacheKey = generateCacheKey(response.config);
        const cachedRequest = requestCache.get(cacheKey);

        if (cachedRequest) {
          requestCache.set(cacheKey, {
            ...cachedRequest,
            response: response,
          });
        }
      }

      // Add warning flag if this was a duplicate request that made it through
      if (response.config._isDuplicateRequest) {
        if (typeof response.data === 'object' && response.data !== null) {
          response.data._internal_warning = 'Twice hit request processed';
        }
      }

      // removeAsyncData('@response_interceptors');
      return response;
    },
    (error) => {
      if (error?.__cachedResponse) {
        return Promise.resolve(error.__cachedResponse);
      }

      try {
        let message = '';
        let endpointUrl = error?.response?.config?.url
          ? error.response.config.url
          : '';
        if (endpointUrl && endpointUrl.includes('/')) {
          endpointUrl = Linking.parse(endpointUrl);
          endpointUrl = endpointUrl?.path ? `/${endpointUrl.path}` : '';
        }
        const errorCode = error?.response?.data?.errorCode
          ? error?.response?.data?.errorCode
          : '';
        const errorMessage = error?.response?.data?.message
          ? error?.response?.data?.message
          : '';
        const excludedEnpointUrl = [
          '/auth/login/mobile',
          '/carts/voucher',
          '/auth/phone/check',
          '/auth/email/check',
          'analytics-api',
          '/fb-conversion',
          '/user/me/carts/stores',
        ];

        message = errorMessage;
        if (/IDENTITY_NUMBER_ALREADY_EXISTS/i.test(errorCode)) {
          message = '';
        }
        const isBabyRegistyError = /baby registry/gi.test(message);

        if (
          isWebPlatform &&
          errorCode === 'PRODUCT_NOT_FOUND' &&
          endpointUrl.indexOf('/v3/products/') > -1
        ) {
          window.prerenderReady = true;
        }

        if (
          (endpointUrl.indexOf('/payments') > -1 ||
            endpointUrl.indexOf('/users/me/orders') > -1) &&
          errorCode === 'payment_failed'
        ) {
          message = 'Payment failed, please try again later';
          storeAsyncData('@payment_failed', 'true');
        }

        if (excludedEnpointUrl.includes(endpointUrl)) {
          message = '';
        }

        if (errorMessage === 'Stores is not available') {
          message = '';
        }
        // ignore error from internal tracking
        if (/[/]sessions[/]me/.test(endpointUrl)) {
          message = '';
        }

        if (error && error.message === 'Network Error') {
          message = 'Network error';
        }
        if (endpointUrl.indexOf('/login') == -1) {
          if (error?.response?.status === 404) {
            storeAsyncData('@response_interceptors', 'error_404');
          } else if (error?.response?.status === 500) {
            storeAsyncData('@response_interceptors', 'error_500');
          } else if (error?.response?.status === 503) {
            storeAsyncData('@response_interceptors', 'error_503');
          }
        }

        if (!message && endpointUrl.indexOf('/feedback-forms') > -1) {
          message = 'Failed to submit feedback.';
        }

        if (
          /session expired|maxAge|jwt issuer invalid|invalid signature/gi.test(
            message,
          )
        ) {
          AsyncStorage.removeItem('userData');
        } else if (
          error?.response?.data?.data?.statusCode === 500 ||
          error?.response?.data?.data?.statusCode === 503 ||
          error?.response?.data?.data?.statusCode === 504
        ) {
          // dont show toast if error status code is 500
        } else {
          if (message) {
            Toast.show({
              text1: message,
              type: 'error',
              ...(isBabyRegistyError && {
                position: 'top',
                topOffset: 80,
              }),
            });
          }
        }
      } catch (err) {
        global.errorLogger.captureException(err);
      }

      return Promise.reject(error);
    },
  );
}
